import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getUserData } from '../services/authService';
import { logAccess } from '../services/accessLogger';
import LoadingState from '../components/shared/LoadingState';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        let attempts = 0;
        let userData = null;
        
        while (attempts < 3 && !userData) {
          try {
            const { data, error } = await getUserData(user.uid);
            
            if (data) {
              userData = data;
            } else if (error) {
              attempts++;
              if (attempts < 3) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          } catch (error) {
            attempts++;
            if (attempts < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (userData) {
          const isGoogleUser = user.providerData.some(p => p.providerId === 'google.com');
          if (isGoogleUser && userData.mustChangePassword) {
            const { updateUserMustChangePassword } = await import('../services/authService');
            await updateUserMustChangePassword(user.uid, false);
            userData.mustChangePassword = false;
          }
          
          try {
            logAccess(
              { ...userData, uid: user.uid },
              'Login',
              { provider: user.providerData[0]?.providerId || 'unknown' }
            );
          } catch (logError) {
            console.warn('⚠️ logAccess falhou:', logError);
          }
        }
        
        setUserData(userData);
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    isRoot: userData?.role === 'root',
    isAdmin: userData?.role === 'admin',
    isOperational: userData?.role === 'operational',
    isCollaborator: userData?.role === 'collaborator',
    isActive: userData?.active === true,
    mustChangePassword: userData?.mustChangePassword === true
  };

  if (loading) {
    return (
      <LoadingState
        variant="screen"
        title="Carregando SDC Turismo"
        subtitle="Validando seu acesso com seguranca"
      />
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
